import type {
  DiamRoleAttribute,
  DiamRoleDetails,
  DiamRoleInclusion,
} from '../../diamApi';
import { getAttributeDescription } from '../IdentityDetails/constants';
import { RolePermissionNode } from './types';

function mapAttributeNode(attribute: DiamRoleAttribute): RolePermissionNode {
  return {
    id: attribute.id,
    label: attribute.name,
    description: getAttributeDescription(attribute.name),
  };
}

function attributeGroupKey(name: string): string {
  const separatorIndex = name.indexOf(':');
  if (separatorIndex <= 0) {
    return 'attributes';
  }

  return name.slice(0, separatorIndex);
}

function mapDirectAttributesTree(
  attributes: readonly DiamRoleAttribute[],
): RolePermissionNode[] {
  const groups = new Map<string, DiamRoleAttribute[]>();

  for (const attribute of attributes) {
    const groupKey = attributeGroupKey(attribute.name);
    const existingGroup = groups.get(groupKey);
    if (existingGroup) {
      existingGroup.push(attribute);
      continue;
    }

    groups.set(groupKey, [attribute]);
  }

  return Array.from(groups.entries()).map(([groupKey, groupAttributes]) => ({
    id: `group:${groupKey}`,
    label: groupKey,
    children: groupAttributes
      .slice()
      .sort((left, right) => left.name.localeCompare(right.name))
      .map(mapAttributeNode),
  }));
}

function inclusionNodeId(inclusion: DiamRoleInclusion, index: number): string {
  const roleId = inclusion.roleId ?? `index-${index}`;
  return `role:${roleId}`;
}

function mapInclusionNode(
  inclusion: DiamRoleInclusion,
  index: number,
): RolePermissionNode {
  const nodeId = inclusionNodeId(inclusion, index);
  const label =
    inclusion.roleName ?? inclusion.roleCode ?? 'Included functional role';
  const attributeNodes = (inclusion.attributes ?? [])
    .slice()
    .sort((left, right) => left.name.localeCompare(right.name))
    .map(mapAttributeNode);

  if (attributeNodes.length > 0) {
    return {
      id: nodeId,
      label,
      description: inclusion.roleCode ?? undefined,
      children: attributeNodes,
    };
  }

  if (inclusion.type === 'ROLE') {
    return {
      id: nodeId,
      label,
      description: inclusion.roleCode
        ? `Includes all attributes from ${inclusion.roleCode}`
        : 'Includes the full functional role',
    };
  }

  return {
    id: nodeId,
    label,
    description: inclusion.roleCode ?? undefined,
  };
}

function mapInclusionsPermissionTree(
  role: DiamRoleDetails,
): RolePermissionNode[] {
  const inclusionNodes = (role.inclusions ?? []).map(mapInclusionNode);
  const directAttributes = role.attributes ?? [];

  if (directAttributes.length === 0) {
    return inclusionNodes;
  }

  return [
    ...inclusionNodes,
    {
      id: 'group:direct-attributes',
      label: 'Direct attributes',
      children: directAttributes
        .slice()
        .sort((left, right) => left.name.localeCompare(right.name))
        .map(mapAttributeNode),
    },
  ];
}

export function mapRolePermissionTree(
  role: DiamRoleDetails,
): RolePermissionNode[] {
  const inclusions = role.inclusions ?? [];
  const attributes = role.attributes ?? [];

  if (inclusions.length > 0) {
    return mapInclusionsPermissionTree(role);
  }

  if (attributes.length > 0) {
    return mapDirectAttributesTree(attributes);
  }

  return [];
}

export function roleDetailsUsesInclusionTree(role: DiamRoleDetails): boolean {
  return (role.inclusions ?? []).length > 0;
}

export function collectSelectedPermissionIds(role: DiamRoleDetails): string[] {
  const selectedIds = new Set<string>();

  for (const attribute of role.attributes ?? []) {
    selectedIds.add(attribute.id);
  }

  for (const [index, inclusion] of (role.inclusions ?? []).entries()) {
    const inclusionAttributes = inclusion.attributes ?? [];

    selectedIds.add(inclusionNodeId(inclusion, index));

    for (const attribute of inclusionAttributes) {
      selectedIds.add(attribute.id);
    }
  }

  return Array.from(selectedIds);
}

export function getDefaultExpandedPermissionIds(
  tree: readonly RolePermissionNode[],
): string[] {
  const expandedIds: string[] = [];

  for (const node of tree) {
    expandedIds.push(node.id);
  }

  return expandedIds;
}
